import { createClient } from '@supabase/supabase-js'

import { classifySignInError } from './auth-guard'

import type { Database } from './database.types'

/**
 * The one Supabase client for the app.
 *
 * Both variables must be present at BUILD time — Vite inlines them into the
 * bundle, so a missing value on Cloudflare Pages produces a white screen with
 * a console error rather than anything self-explanatory. Failing loudly here
 * turns that into a message that names the variable.
 */

function required(name: string, value: string | undefined): string {
  if (typeof value === 'string' && value.length > 0) return value

  throw new Error(
    `Missing ${name}.\n\n` +
      'Locally: copy .env.example to .env and fill it in, then restart the dev server.\n' +
      'On Cloudflare Pages: add it under Settings → Environment variables as a ' +
      'BUILD variable, then redeploy. Runtime variables are not enough — Vite ' +
      'reads these while building, not in the browser.',
  )
}

const supabaseUrl = required('VITE_SUPABASE_URL', import.meta.env.VITE_SUPABASE_URL)
const supabaseAnonKey = required('VITE_SUPABASE_ANON_KEY', import.meta.env.VITE_SUPABASE_ANON_KEY)

/*
 * The generated schema types are supplied here, which is the only place that
 * makes them do anything. Without the parameter every from().select() came
 * back effectively untyped, so the casts in the data layer asserted shapes
 * over `any` and checked nothing -- and database.types.ts had drifted three
 * migrations behind without a single compile error to show for it.
 */
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Keeps the session across reloads and refreshes it before expiry, which
    // is what stops a long day in one tab ending in a surprise sign-out.
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    /*
     * Implicit, not PKCE, and the reason is the email links.
     *
     * Under PKCE, resetPasswordForEmail attaches a code challenge and keeps
     * the matching verifier in THIS browser's storage. Two things follow, and
     * both are fatal here:
     *
     *   - the emailed token is a pkce_ hash, and verifyOtp posts to /verify
     *     with no verifier, so it cannot redeem one;
     *   - even where it could, only the browser that ASKED for the reset holds
     *     the verifier. Somebody who requests a reset on a laptop and opens
     *     the mail on their phone could never finish, which is not a rule a
     *     password reset is allowed to have.
     *
     * Nothing here ever called exchangeCodeForSession, so PKCE was only being
     * completed incidentally by detectSessionInUrl. Implicit gives a plain
     * token hash that verifyOtp redeems from any device, which is what makes
     * the scanner-proof link in the email templates work at all.
     */
    flowType: 'implicit',
  },
  realtime: {
    // One event per second per channel is plenty for start/stop, and keeps a
    // misbehaving tab from flooding the socket.
    params: { eventsPerSecond: 1 },
  },
})

/**
 * Whatever an auth link left in the URL, read before supabase-js clears it.
 *
 * detectSessionInUrl strips the fragment the moment it has looked at it, so a
 * screen that checks later finds a bare URL and cannot tell a link that was
 * spent from one that was never there. Reading it here, at import time, is
 * the only point at which the reason is still present.
 */
export const authLinkError: { code: string | null; description: string | null } | null = (() => {
  const params = (s: string) => new URLSearchParams(s.replace(/^[#?]/, ''))
  const hash = params(window.location.hash)
  const query = params(window.location.search)

  const code = hash.get('error_code') ?? query.get('error_code')
  const description = hash.get('error_description') ?? query.get('error_description')
  const error = hash.get('error') ?? query.get('error')

  if (!code && !description && !error) return null
  return { code: code ?? error, description }
})()

/** Where invite and reset links come back to. */
export const siteUrl = window.location.origin

/**
 * Is this the account's current password?
 *
 * GoTrue has no "check a password" endpoint, so the only honest test is to
 * sign in with it. That is done on a SEPARATE, throwaway client, and every
 * part of that matters:
 *
 *   - persistSession is off and the storage key is its own, so nothing this
 *     does can touch the session the person is already using. The real client
 *     never sees it, and a failed check leaves no trace at all.
 *   - signOut passes scope 'local', which revokes ONLY the session this check
 *     just created. The default scope is 'global', which would revoke every
 *     session the account has -- including the one running this code. That
 *     one argument is the difference between checking a password and signing
 *     somebody out of all their devices.
 *   - a wrong password changes nothing: GoTrue answers with an error and no
 *     session is issued.
 *
 * On an account with two-factor the new session is only AAL1. That is fine --
 * the question asked here is whether the password is right, not whether to
 * trust the session, and the session is discarded either way.
 */
export async function verifyPassword(
  email: string,
  password: string,
): Promise<'ok' | 'wrong' | 'problem'> {
  const probe = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'cadence.password-check',
    },
  })

  const { error } = await probe.auth.signInWithPassword({ email, password })
  const verdict = classifySignInError(error)

  if (verdict === 'ok') {
    // Scope matters; see above. Failing to tidy up is not a reason to reject a
    // password that was just proved correct, so this never throws -- the stray
    // session expires on its own.
    try {
      await probe.auth.signOut({ scope: 'local' })
    } catch {
      /* the check already succeeded; the leftover session is harmless */
    }
  }

  return verdict
}
